import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Broadcast {
  id: string;
  name: string;
  message_text: string;
  message_format: string;
  buttons: { text: string; url?: string; callback_data?: string }[];
  media_type: string | null;
  media_url: string | null;
  segment_filter: Record<string, any>;
  send_type: string;
  scheduled_at: string | null;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  blocked_count: number;
  clicked_count: number;
  purchases_count: number;
  revenue_rub: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useBroadcasts() {
  return useQuery({
    queryKey: ['broadcasts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('broadcasts')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Broadcast[];
    },
  });
}

export function useBroadcast(id: string | null) {
  return useQuery({
    queryKey: ['broadcast', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('broadcasts')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as Broadcast;
    },
  });
}

export function useCreateBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (broadcast: Partial<Broadcast>) => {
      const { data, error } = await supabase
        .from('broadcasts')
        .insert(broadcast as any)
        .select()
        .single();
      if (error) throw error;
      return data as Broadcast;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['broadcasts'] }),
  });
}

export function useUpdateBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Broadcast> & { id: string }) => {
      const { error } = await supabase.from('broadcasts').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
    },
  });
}

export function useDeleteBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('broadcasts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['broadcasts'] }),
  });
}

export function useBroadcastStats(broadcastId: string | null) {
  return useQuery({
    queryKey: ['broadcast-stats', broadcastId],
    enabled: !!broadcastId,
    queryFn: async () => {
      const [logsRes, clicksRes] = await Promise.all([
        supabase
          .from('broadcast_logs')
          .select('status')
          .eq('broadcast_id', broadcastId!),
        supabase
          .from('broadcast_clicks')
          .select('id')
          .eq('broadcast_id', broadcastId!),
      ]);

      const logs = logsRes.data || [];
      const sent = logs.filter(l => l.status === 'sent' || l.status === 'delivered').length;
      const failed = logs.filter(l => l.status === 'failed').length;
      const blocked = logs.filter(l => l.status === 'blocked').length;
      const clicks = clicksRes.data?.length || 0;
      const ctr = sent > 0 ? Math.round((clicks / sent) * 100) : 0;

      return { total: logs.length, sent, failed, blocked, clicks, ctr };
    },
  });
}

export function useEstimateRecipients() {
  return useMutation({
    mutationFn: async (filter: Record<string, any>): Promise<number> => {
      let query = supabase.from('telegram_users').select('id', { count: 'exact', head: true });

      if (filter.has_purchased === true) {
        query = query.not('last_purchase_at', 'is', null);
      } else if (filter.has_purchased === false) {
        query = query.is('last_purchase_at', null);
      }

      if (filter.registered_after) {
        query = query.gte('created_at', filter.registered_after);
      }
      if (filter.registered_before) {
        query = query.lte('created_at', filter.registered_before);
      }

      if (filter.utm_source) {
        query = query.eq('utm_source', filter.utm_source);
      }
      if (filter.utm_campaign) {
        query = query.eq('utm_campaign', filter.utm_campaign);
      }

      if (filter.min_generations !== undefined && filter.min_generations > 0) {
        query = query.gte('total_generations', filter.min_generations);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    },
  });
}

export function useSendBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (broadcastId: string) => {
      const { data, error } = await supabase.functions.invoke('send-broadcast', {
        body: { broadcast_id: broadcastId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
    },
  });
}

export function useSendTestBroadcast() {
  return useMutation({
    mutationFn: async (params: {
      telegram_id?: number;
      username?: string;
      message_text: string;
      message_format: string;
      media_type?: string | null;
      media_url?: string | null;
      buttons?: { text: string; url?: string; callback_data?: string }[];
    }) => {
      const { data, error } = await supabase.functions.invoke('send-test-broadcast', {
        body: params,
      });
      if (error) throw error;
      return data;
    },
  });
}
